// Shared branding for client-side HTML prints (the ones that open a window
// and call print(), rather than downloading a server-rendered PDF). Keeps the
// company logo+name header and the "Generated by Fritech Ai Business Platform"
// footer identical to the server PDFs. Any new client print should drop in
// `printBrandingCss` + `companyPrintHeader(company)` + `printFooter()`.
import { PRODUCT_NAME } from '../branding';

type CompanyDetails = { address?: string; phone?: string; email?: string; website?: string; tax_id?: string; registration_no?: string };
type Company = { id: string; name: string; code?: string; is_demo?: boolean; print_logo_height?: number; details?: CompanyDetails } | null | undefined;

function esc(s: unknown): string {
  return String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
}

// Address / phone·email·website / tax-id block under the company name.
function detailLines(d?: CompanyDetails): string {
  if (!d) return '';
  const parts: string[] = [];
  if (d.address) parts.push(`<div class="np-co-addr">${esc(d.address)}</div>`);
  const contact = [d.phone, d.email, d.website].filter(Boolean).map((x) => esc(x)).join(' · ');
  if (contact) parts.push(`<div>${contact}</div>`);
  const ids = [d.tax_id ? `Tax ID: ${esc(d.tax_id)}` : '', d.registration_no ? `Reg. No: ${esc(d.registration_no)}` : ''].filter(Boolean).join(' · ');
  if (ids) parts.push(`<div>${ids}</div>`);
  return parts.length ? `<div class="np-co-det">${parts.join('')}</div>` : '';
}

// A demo company's printouts carry this across every page, with no way to turn it
// off (owner's request). Same as the server's PDFs (services/demo-watermark.js),
// plain text for the same reason: Chromium does not repeat a fixed <svg> on every page.
export const DEMO_WATERMARK_TEXT = 'DEMO COMPANY';
export const DEMO_WATERMARK_HTML = `<div data-demo-watermark="" aria-hidden="true" style="position:fixed;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-45deg);font:700 11vmin Arial,Helvetica,sans-serif;color:rgba(100,116,139,.2);white-space:nowrap;z-index:2147483647;pointer-events:none;">${DEMO_WATERMARK_TEXT}</div>`;

// Logo is served (auth-free) at /api/v1/companies/:id/logo. Use an absolute
// URL because the print window is about:blank with no usable base href.
export function companyPrintHeader(company: Company): string {
  if (!company) return '';
  const logo = `${window.location.origin}/api/v1/companies/${company.id}/logo`;
  const h = company.print_logo_height || 48;
  return `${company.is_demo ? DEMO_WATERMARK_HTML : ''}<div class="np-co-head">
    <img src="${esc(logo)}" alt="" onerror="this.style.display='none'" style="height:${h}px;max-height:${h}px;max-width:260px;object-fit:contain" />
    <div class="np-co-meta">
      <div class="np-co-name">${esc(company.name)}</div>
      ${company.code ? `<div class="np-co-code">${esc(company.code)}</div>` : ''}
      ${detailLines(company.details)}
    </div>
  </div>`;
}

export function printFooter(): string {
  return `<div class="np-foot">Generated by ${PRODUCT_NAME}</div>`;
}

export const printBrandingCss = `
  .np-co-head{display:flex;align-items:center;gap:12px;border-bottom:2px solid #e2e8f0;padding-bottom:10px;margin-bottom:16px}
  .np-co-head img{object-fit:contain}
  .np-co-name{font-size:16px;font-weight:700;color:#0f172a}
  .np-co-code{font-size:11px;color:#64748b}
  .np-co-det{font-size:10px;color:#64748b;line-height:1.4;margin-top:3px}
  .np-co-addr{white-space:pre-line}
  .np-foot{margin-top:28px;text-align:center;font-size:10px;color:#64748b}
  /* Pin the attribution to the bottom-center of every printed page (Word-style),
     in the page margin so body content never overlaps it. */
  @media print{.np-foot{position:fixed;left:0;right:0;bottom:10px;margin:0}}
`;
